import React, { useEffect, useState, useRef } from 'react'
import {
  Button,
  IconButton,
  TextLink,
  SimpleCard,
  ChecklistProgress,
  GiftTrackerProgress,
} from 'baby-design'
import { EditPencil, Lock, LockOpen, Eye, Share } from 'baby-design/icons'
import classNames from 'classnames'
import {
  registryViewAsGuestPath,
  registryChecklistPath,
  registryGiftTrackerPath,
} from 'lib/urls'
import { RegistryProfileIcon } from 'components/icons'
import { useRegistryChecklistCategories } from 'src/routes/(registry)/registry/checklist/components/api/hooks'
import { useNavigate } from 'react-router-dom-v5-compat'
import NiceModal from '@ebay/nice-modal-react'
import { track, useTracking } from 'lib/analytics'
import useCurrentUser from 'shared/hooks/useCurrentUser/useCurrentUser'
import ShareYourRegistryDrawer from './components/ShareYourRegistryDrawer/ShareYourRegistryDrawer'
import RegistryDetailsEditModal from './components/RegistryDetailsEditModal/RegistryDetailsEditModal'
import PrivacySettingsEditModal from './components/PrivacySettingsEditModal/PrivacySettingsEditModal'
import { RegistryViewHeaderProps } from './RegistryViewHeader.types'
import css from './RegistryViewHeader.styles.scss'

const RegistryViewHeader: React.FC<RegistryViewHeaderProps> = ({
  currentRegistry,
}) => {
  const { title, photoUrl, searchLevel, description } = currentRegistry || {}
  const [currentUser] = useCurrentUser()
  const tracker = useTracking()
  const navigate = useNavigate()
  const [expanded, setExpanded] = useState(false)
  const [showControls, setShowControls] = useState(true)
  const descriptionRef = useRef<HTMLDivElement>(null)
  const [registryDetailsEditModalIsOpen, setRegistryDetailsEditModalIsOpen] =
    useState(false)
  const [privacySettingsEditModalIsOpen, setPrivacySettingsEditModalIsOpen] =
    useState(false)
  let percentChecklistComplete = 0
  let percentGiftTrackerComplete = 0
  const { isLoading: checklistLoading, data: checklistData } =
    useRegistryChecklistCategories(currentRegistry?.id)
  const { completedCategoriesCount, totalCategoriesCount } = checklistData

  if (!checklistLoading && checklistData && totalCategoriesCount) {
    percentChecklistComplete = Math.round(
      (completedCategoriesCount / totalCategoriesCount) * 100
    )
  }
  if (currentRegistry && currentRegistry?.regItemsCount) {
    percentGiftTrackerComplete = Math.round(
      (currentRegistry?.reservationCount / currentRegistry?.regItemsCount) * 100
    )
  }

  const handleClickEditRegistry = () => setRegistryDetailsEditModalIsOpen(true)
  const handleClickPrivacyStatus = () => {
    tracker.trackEvent({
      event: track.visibilityPrivacyClicked,
      eventLocation: track.EventLocation.REGISTRY,
      registryId: currentRegistry.id,
      registryOwnerId: currentRegistry.ownerId,
      listType: currentRegistry.type || 'baby_registry',
      isRegistryOwner: currentRegistry.ownerId === currentUser?.id,
    })
    setPrivacySettingsEditModalIsOpen(true)
  }
  const handleClickShare = () => {
    NiceModal.show(ShareYourRegistryDrawer, {
      onPreviewClick: handleClickPreview,
      registryLink: registryShareUrl,
    })
  }
  const handleClickPreview = () => {
    tracker.trackEvent({
      event: track.guestViewClicked,
      eventLocation: track.EventLocation.REGISTRY,
      registryId: currentRegistry.id,
      registryOwnerId: currentRegistry.ownerId,
      listType: currentRegistry.type || 'baby_registry',
      isRegistryOwner: currentRegistry.ownerId === currentUser?.id,
    })

    window.open(registryViewAsGuestPath(currentRegistry.urlSlug), '_blank')
  }

  const shouldShowDescriptionControls = () => {
    const descriptionElement = descriptionRef.current
    if (
      descriptionElement &&
      descriptionElement.scrollHeight > descriptionElement.clientHeight
    ) {
      setShowControls(true)
    } else {
      setShowControls(false)
    }
  }

  useEffect(() => {
    shouldShowDescriptionControls()
    setExpanded(false)
  }, [description])

  const descriptionElem = (
    <>
      <p
        className={css.RegistryViewHeader__greeting}
        dangerouslySetInnerHTML={{ __html: description || '' }}
        ref={descriptionRef}
      />
      {description && (showControls || expanded) && (
        <TextLink
          className={css.RegistryViewHeader__greetingToggleTextLink}
          size="md"
          variant="default"
          onClick={() => {
            setExpanded(!expanded)
          }}
        >
          {expanded ? 'see less' : 'see more'}
        </TextLink>
      )}
    </>
  )

  const privacyStatusElem = () => {
    const isPrivate = searchLevel === 'private'
    const displayText = isPrivate ? 'private' : 'public'
    return (
      <div className={css.RegistryViewHeader__privacyStatus}>
        {isPrivate ? (
          <Lock className={css.RegistryViewHeader__privacyStatusIcon} />
        ) : (
          <LockOpen className={css.RegistryViewHeader__privacyStatusIcon} />
        )}
        <p>
          Your registry is <span className="sr-only">{displayText}.</span>
          <TextLink
            aria-label="Edit Registry Visibility Settings."
            className={css.RegistryViewHeader__privacyStatusTextLink}
            role="button"
            size="md"
            variant="default"
            onClick={handleClickPrivacyStatus}
          >
            {displayText}
          </TextLink>
        </p>
      </div>
    )
  }

  const registryImageElem = (
    <button
      aria-label="Edit Registry Details"
      className={css.RegistryViewHeader__avatar}
      type="button"
      onClick={handleClickEditRegistry}
    >
      {photoUrl ? (
        <img alt="Profile Picture Avatar" src={photoUrl} />
      ) : (
        <RegistryProfileIcon height={80} width={80} />
      )}
    </button>
  )

  const registryGreetingElem = (
    <div
      className={classNames(css.RegistryViewHeader__greetingContainer, {
        [css['RegistryViewHeader__greetingContainer--expanded']]: expanded,
      })}
    >
      {!title && <div className={css.shimmer} />}
      {descriptionElem}
    </div>
  )

  const titleContainerElem = (
    <div className={css.RegistryViewHeader__titleContainer}>
      {title ? (
        <div className={css.RegistryViewHeader__titleAndEdit}>
          <h1>
            {title}
            <IconButton
              aria-label="Edit Registry Details"
              size="sm"
              variant="standard-default"
              onClick={handleClickEditRegistry}
            >
              <EditPencil />
            </IconButton>
          </h1>
        </div>
      ) : (
        <div className={css.shimmer} />
      )}
      {privacyStatusElem()}
    </div>
  )

  const handleChecklistClick = () => {
    tracker.trackEvent({
      checklistType: 1,
      event: track.checklistViewed,
      eventLocation: track.EventLocation.REGISTRY,
      registryId: currentRegistry.id,
      registryOwnerId: currentRegistry.ownerId,
      listType: currentRegistry.type || 'baby_registry',
      isRegistryOwner: currentRegistry.ownerId === currentUser?.id,
    })
    navigate(registryChecklistPath)
  }

  const handleGiftTrackerClick = () => {
    tracker.trackEvent({
      checklistType: 1,
      event: track.giftTrackerClicked,
      eventLocation: track.EventLocation.REGISTRY,
      registryId: currentRegistry.id,
      registryOwnerId: currentRegistry.ownerId,
      listType: currentRegistry.type || 'baby_registry',
      isRegistryOwner: currentRegistry.ownerId === currentUser?.id,
    })
    navigate(registryGiftTrackerPath)
  }

  const registryShareUrl =
    currentRegistry?.url?.match(/https?:\/\/(.*)/)?.[1] || ''

  return (
    <div className={css.RegistryViewHeader}>
      <div className={css.RegistryViewHeader__container}>
        <div className={css.RegistryViewHeader__details}>
          <div className={css.RegistryViewHeader__registryInfo}>
            <div className={css.RegistryViewHeader__registryHeading}>
              {registryImageElem}
              <div className={css.RegistryViewHeader__titleAndActions}>
                {titleContainerElem}
                <div className={css.buttonContainer}>
                  <div className={css.buttonRow}>
                    <Button
                      className={css.buttonRowTextSecondaryButton}
                      icon={{ side: 'left', icon: <Share /> }}
                      size="md"
                      variant="text-secondary"
                      onClick={handleClickShare}
                    >
                      Share
                    </Button>
                    <Button
                      className={css.buttonRowTextSecondaryButton}
                      icon={{ side: 'left', icon: <Eye /> }}
                      size="md"
                      variant="text-secondary"
                      onClick={handleClickPreview}
                    >
                      Guest View
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            {registryGreetingElem}
          </div>
        </div>
        <div className={css.RegistryViewHeader__mobileActions}>
          <div className={css.RegistryViewHeader__checklist}>
            <SimpleCard
              content={
                currentRegistry ? (
                  `${percentChecklistComplete}%`
                ) : (
                  <span className={css.shimmer} />
                )
              }
              leftIcon={<ChecklistProgress value={percentChecklistComplete} />}
              role="link"
              title="Checklist"
              onClick={() => handleChecklistClick()}
            />
          </div>
          <div className={css.RegistryViewHeader__giftTracker}>
            <SimpleCard
              content={
                currentRegistry ? (
                  `${currentRegistry.reservationCount}`
                ) : (
                  <span className={css.shimmer} />
                )
              }
              leftIcon={
                <GiftTrackerProgress value={percentGiftTrackerComplete} />
              }
              role="link"
              title="Purchased"
              onClick={() => handleGiftTrackerClick()}
            />
          </div>
        </div>
      </div>
      <PrivacySettingsEditModal
        currentRegistry={currentRegistry}
        handleClose={() => setPrivacySettingsEditModalIsOpen(false)}
        isOpen={privacySettingsEditModalIsOpen}
      />
      <RegistryDetailsEditModal
        currentRegistry={currentRegistry}
        handleClose={() => setRegistryDetailsEditModalIsOpen(false)}
        isOpen={registryDetailsEditModalIsOpen}
      />
    </div>
  )
}

export default RegistryViewHeader
